import { ConfigProvider, Progress, Tag, Tooltip, Typography } from 'antd'
import { ThemeConfig } from 'antd/es/config-provider/context'
import { IndicationClassification } from 'features/indicationTriage'
import { useGeckoLocale } from 'features/locale'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { UNKNOWN_COLOR_SCHEME } from './indicationClassificationColors'
import { IndicationClassificationConfigContext } from './indicationClassifier'

// Define a type for ColorState
export interface IndicationColorState {
  colorBgChecked: string
  colorBgUnchecked: string
  colorText: string
}

export const createClassificationCheckableTagStyle = (
  tagColorTheme: IndicationColorState,
  tagSelected: boolean
): React.CSSProperties => {
  return {
    width: '100%',
    cursor: 'pointer',
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: tagSelected
      ? tagColorTheme.colorBgChecked
      : tagColorTheme.colorBgUnchecked,
  }
}

export const createClassificationAntConfig: (
  colors: IndicationColorState
) => ThemeConfig = (colorTheme) => {
  return {
    components: {
      Tag: {
        colorPrimary: colorTheme.colorBgChecked, // Color for background
        colorText: colorTheme.colorText, // Color for Text
        colorFillSecondary: colorTheme.colorBgUnchecked, // Bg color for hover
      },
      Progress: {
        defaultColor: GeckoTheme.forecast.icon2,
      },
    },
  }
}

export interface IndicationClassificationTagGaugeProps {
  gaugeMin: number
  gaugeMax: number
  value: number
}

const RawIndicationClassificationTagGauge: React.FC<
  IndicationClassificationTagGaugeProps
> = ({ value, gaugeMin, gaugeMax }) => {
  const percent = useMemo(
    () => (100 * (value - gaugeMin)) / (gaugeMax - gaugeMin),
    [gaugeMax, gaugeMin, value]
  )
  return (
    <Progress
      type='circle'
      percent={Math.abs(percent)}
      size={20}
      style={{ margin: '0.1rem' }}
      status='normal'
      showInfo={false}
    />
  )
}
export const IndicationClassificationTagGauge = React.memo(
  RawIndicationClassificationTagGauge
)

export interface IndicationClassificationTagProps {
  classification: IndicationClassification
  /**
   * Reference measurement for the gauge. Optional: verdicts without a plottable
   * reference (e.g. FFS outcomes) render as a label-only tag with no gauge.
   */
  value?: GeckoMeasurement
  tooltipPrefix?: string
}

const { Text } = Typography

const IndicationTriageListTagContainer = styled.div`
  width: 6rem;
`

const TagFlexStyle = {
  display: 'flex',
  gap: '0.3rem',
  alignItems: 'center',
}

export const BlankIndicationClassificationTag: React.FC = () => {
  const tagStyle = useMemo(() => {
    return {
      ...createClassificationCheckableTagStyle(UNKNOWN_COLOR_SCHEME, true),
      ...TagFlexStyle,
    }
  }, [])
  const themeConfig = useMemo(
    () => createClassificationAntConfig(UNKNOWN_COLOR_SCHEME),
    []
  )

  return (
    <ConfigProvider theme={themeConfig}>
      <IndicationTriageListTagContainer>
        <Tooltip title='UNKNOWN CLASSIFICATION'>
          <Tag.CheckableTag style={tagStyle} checked>
            <Text>UNKNOWN</Text>
          </Tag.CheckableTag>
        </Tooltip>
      </IndicationTriageListTagContainer>
    </ConfigProvider>
  )
}

const RawIndicationClassificationTag: React.FC<
  IndicationClassificationTagProps
> = ({ classification, tooltipPrefix, value }) => {
  const { colors } = useContext(IndicationClassificationConfigContext)
  const tagColorTheme = useMemo(() => {
    return colors[classification] ?? UNKNOWN_COLOR_SCHEME
  }, [classification, colors])
  const tagStyle = useMemo(() => {
    return {
      ...createClassificationCheckableTagStyle(tagColorTheme, true),
      ...TagFlexStyle,
      cursor: 'default',
    }
  }, [tagColorTheme])

  const themeConfig = useMemo(
    () => createClassificationAntConfig(tagColorTheme),
    [tagColorTheme]
  )
  const { formatMeasurement } = useGeckoLocale()

  return (
    <ConfigProvider theme={themeConfig}>
      <IndicationTriageListTagContainer>
        <Tooltip
          title={
            <>
              {tooltipPrefix ? `${tooltipPrefix}: ` : ''}
              {value ? (
                <span>
                  {formatMeasurement({ measurement: value, variant: 'none' })}
                </span>
              ) : null}
            </>
          }>
          <Tag.CheckableTag style={tagStyle} checked>
            {value?.unit === GeckoMeasurementUnit.Percent ? (
              <IndicationClassificationTagGauge
                gaugeMin={0}
                gaugeMax={100}
                value={value.num}
              />
            ) : (
              <></>
            )}
            <Text>{classification}</Text>
          </Tag.CheckableTag>
        </Tooltip>
      </IndicationTriageListTagContainer>
    </ConfigProvider>
  )
}

export const IndicationClassificationTag = React.memo(
  RawIndicationClassificationTag
)
